import React, { useState, useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillTableBetter from 'quill-table-better';
import 'quill-table-better/dist/quill-table-better.css';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { Building, ApartmentGroup } from '../types';
import { buildingsApi } from '../api/buildingsApi';
import { apartmentsApi, Apartment as ApartmentOption } from '../api/apartmentsApi';
import { sendEmailNotification } from '../api/emailNotificationsApi';
import {
  applyDefaultBuildingSelection,
  getBuildingSelectProps,
  needsBuildingSelection,
  persistSelectedBuilding,
  selectBuilding,
} from '../utils/buildingSelection';
import toast from 'react-hot-toast';
import ApartmentGroupMultiSelect from '../components/apartments/ApartmentGroupMultiSelect';
import ApartmentMultiSelect from '../components/apartments/ApartmentMultiSelect';

// Register the table-better module with more explicit registration
console.log('QuillTableBetter:', QuillTableBetter);
console.log('Quill version:', Quill.version);

// Register the module
Quill.register('modules/table-better', QuillTableBetter, true);

// Additional CSS for proper table rendering
const tableStyles = `
  .ql-editor table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 10px 0;
  }
  
  .ql-editor table td,
  .ql-editor table th {
    border: 1px solid #000;
    padding: 5px 10px;
    min-width: 50px;
    min-height: 20px;
    position: relative;
  }
  
  .ql-editor table th {
    background-color: #f1f3f4;
    font-weight: bold;
  }
  
  .ql-editor .ql-table-better {
    border: 1px solid #ccc;
  }
  
  .ql-editor .ql-table-better td {
    border: 1px solid #ccc;
    padding: 5px;
    min-width: 50px;
    min-height: 20px;
    position: relative;
  }
  
  .ql-editor .ql-table-better .ql-table-better-cell-selected {
    background-color: #b3d9ff;
  }
  
  .ql-table-better__wrapper {
    position: relative;
  }
  
  .ql-table-better__menu {
    position: absolute;
    z-index: 1000;
    border: 1px solid #ccc;
    background: white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  
  .ql-table-better__menu-item {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
  }
  
  .ql-table-better__menu-item:hover {
    background-color: #f5f5f5;
  }
  
  .ql-table-better__menu-item:last-child {
    border-bottom: none;
  }
`;

export const SendEmail: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [apartments, setApartments] = useState<ApartmentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Add ref exactly like the GitHub example
  const quillRef = useRef(null);
  const initValue = '';
  
  const [formData, setFormData] = useState({
    buildingId: '',
    selectedGroupIds: [] as string[],
    selectedApartmentIds: [] as string[],
    sendToAll: false,
    deliveryMethods: ['EMAIL'] as string[],
    recipientType: 'OWNER',
    titleImage: '',
    subject: '',
    content: initValue,
    attachments: [] as File[]
  });

  // Quill modules configuration exactly like the GitHub repository
  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        ['table-better'],
        ['clean']
      ]
    },
    table: false,
    'table-better': {
      language: 'en_US',
      menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
      toolbarTable: true,
      operationMenu: {
        items: {
          unmergeCells: {
            text: 'Another unmerge cells name'
          }
        },
        color: {
          colors: ['green', 'red', 'yellow', 'blue', 'white'],
          text: 'Background Colors:'
        }
      }
    },
    keyboard: {
      bindings: QuillTableBetter.keyboardBindings
    },
    clipboard: {
      matchVisual: false,
    }
  };

  console.log('Modules configuration:', modules);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'indent',
    'link', 'image',
    'color', 'background',
    'align', 'script',
    'code-block',
    // Table-better formats
    'table-cell', 'table-cell-block', 'table-header', 'table-list'
  ];

  useEffect(() => {
    // Register QuillTableBetter module
    Quill.register('modules/table-better', QuillTableBetter, true);
    
    loadBuildings();
  }, [user?.id]);

  useEffect(() => {
    if (formData.buildingId) {
      loadApartmentGroups(formData.buildingId);
      loadApartments(formData.buildingId);
    } else {
      setApartmentGroups([]);
      setApartments([]);
    }
  }, [formData.buildingId]);

  const loadApartments = async (buildingId: string) => {
    try {
      const apartmentList = await apartmentsApi.getApartments({ buildingId });
      setApartments(apartmentList.filter((apt) => apt.status === 'ACTIVE'));
    } catch (error) {
      console.error('Error loading apartments:', error);
      setApartments([]);
    }
  };

  const loadBuildings = async () => {
    try {
      setLoading(true);
      const response = await buildingsApi.getBuildings();
      const list = response as unknown as Building[];
      setBuildings(list);
      const defaultBuildingId = applyDefaultBuildingSelection(list, user, '');
      if (defaultBuildingId) {
        persistSelectedBuilding(user?.id, defaultBuildingId);
      }
      setFormData((prev) => ({
        ...prev,
        buildingId: defaultBuildingId,
        selectedGroupIds: [],
        selectedApartmentIds: [],
      }));
    } catch (error) {
      console.error('Error loading buildings:', error);
      toast.error('Không thể tải danh sách chung cư');
    } finally {
      setLoading(false);
    }
  };

  const loadApartmentGroups = async (buildingId: string) => {
    try {
      const filteredGroups = await apartmentsApi.getApartmentGroups(buildingId);
      setApartmentGroups(
        filteredGroups.filter((group) => group.status === 'ACTIVE') as unknown as ApartmentGroup[],
      );
      setFormData((prev) => ({
        ...prev,
        selectedGroupIds: [],
        selectedApartmentIds: [],
      }));
    } catch (error) {
      console.error('Error loading apartment groups:', error);
      toast.error('Không thể tải danh sách nhóm căn hộ');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'buildingId') {
      selectBuilding(user?.id, value, (buildingId) => {
        setFormData((prev) => ({
          ...prev,
          buildingId,
          selectedGroupIds: [],
          selectedApartmentIds: [],
        }));
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEditorChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 2) {
      toast.error('Tối đa 2 files');
      return;
    }
    
    // Check file types
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Chỉ chấp nhận file ảnh, PDF, Word, Excel');
      return;
    }
    
    setFormData(prev => ({ ...prev, attachments: files }));
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.deliveryMethods.length === 0) {
      toast.error('Vui lòng chọn ít nhất một phương thức gửi');
      return;
    }
    
    if (!formData.buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    
    if (
      !formData.sendToAll &&
      formData.selectedGroupIds.length === 0 &&
      formData.selectedApartmentIds.length === 0
    ) {
      toast.error('Vui lòng chọn ít nhất một nhóm căn hộ, căn hộ cụ thể, hoặc tick "Gửi cho tất cả các căn hộ"');
      return;
    }
    
    if (!formData.subject.trim()) {
      toast.error('Vui lòng nhập tiêu đề');
      return;
    }
    
    if (formData.subject.length > 255) {
      toast.error('Tiêu đề không được vượt quá 255 ký tự');
      return;
    }
    
    // Fix the content validation - React Quill returns HTML, so we need to check for meaningful content
    const contentText = formData.content.replace(/<[^>]*>/g, '').trim(); // Strip HTML tags
    if (!contentText || contentText === '' || formData.content === '<p><br></p>') {
      toast.error('Vui lòng nhập nội dung');
      return;
    }

    try {
      setSending(true);
      await sendEmailNotification({
        subject: formData.subject,
        content: formData.content,
        buildingId: formData.buildingId,
        apartmentGroupIds:
          formData.sendToAll || formData.selectedApartmentIds.length > 0
            ? undefined
            : formData.selectedGroupIds,
        apartmentIds:
          formData.sendToAll || formData.selectedApartmentIds.length === 0
            ? undefined
            : formData.selectedApartmentIds,
        sendToAll: formData.sendToAll,
        deliveryMethods: formData.deliveryMethods,
        recipientType: formData.recipientType,
        titleImage: formData.titleImage || undefined,
        attachments: formData.attachments.length > 0 ? formData.attachments : undefined
      });
      
      const methodText = formData.deliveryMethods.includes('EMAIL') && formData.deliveryMethods.includes('APP') 
        ? 'email và ứng dụng' 
        : formData.deliveryMethods.includes('EMAIL') 
        ? 'email' 
        : 'ứng dụng';
      
      toast.success(`Gửi thông báo qua ${methodText} thành công!`);
      
      const restoredBuildingId = applyDefaultBuildingSelection(buildings, user, '');
      // Reset form (keep selected building)
      setFormData({
        buildingId: restoredBuildingId,
        selectedGroupIds: [],
        selectedApartmentIds: [],
        sendToAll: false,
        deliveryMethods: ['EMAIL'],
        recipientType: 'OWNER',
        titleImage: '',
        subject: '',
        content: initValue,
        attachments: []
      });
    } catch (error: any) {
      console.error('Error sending notification:', error);
      toast.error(error.message || 'Không thể gửi thông báo');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <style dangerouslySetInnerHTML={{ __html: tableStyles }} />
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">TIỆN ÍCH</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">Gửi thông báo</span>
      </nav>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gửi thông báo</h1>
        <p className="text-gray-600 mt-1">Gửi thông báo đến cư dân qua email và ứng dụng di động</p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Recipient Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
            Đối tượng nhận thông báo
          </h2>
          
          <div className="space-y-4">
            {/* Delivery Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phương thức gửi <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="delivery-email"
                    checked={formData.deliveryMethods.includes('EMAIL')}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        deliveryMethods: checked 
                          ? [...prev.deliveryMethods.filter(m => m !== 'EMAIL'), 'EMAIL']
                          : prev.deliveryMethods.filter(m => m !== 'EMAIL')
                      }));
                    }}
                    className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <label htmlFor="delivery-email" className="ml-3 text-sm text-gray-700">
                    📧 Gửi đến email: Hệ thống sẽ tự động gửi đến các email chính được lưu tại thông tin căn hộ
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="delivery-app"
                    checked={formData.deliveryMethods.includes('APP')}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        deliveryMethods: checked 
                          ? [...prev.deliveryMethods.filter(m => m !== 'APP'), 'APP']
                          : prev.deliveryMethods.filter(m => m !== 'APP')
                      }));
                    }}
                    className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <label htmlFor="delivery-app" className="ml-3 text-sm text-gray-700">
                    📱 Gửi đến ứng dụng: Chỉ những cư dân có sử dụng App mới nhận được thông báo
                  </label>
                </div>
              </div>
            </div>

            {/* Building Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn chung cư <span className="text-red-500">*</span>
              </label>
              <select
                name="buildingId"
                value={formData.buildingId}
                onChange={handleInputChange}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100"
                required
              >
                {getBuildingSelectProps(buildings).showPlaceholder ? (
                  <option value="">-- Chọn chung cư --</option>
                ) : null}
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Apartment Group Selection */}
            <ApartmentGroupMultiSelect
              groups={apartmentGroups.map((group) => ({ id: group.id, name: group.name }))}
              selectedGroupIds={formData.selectedGroupIds}
              onChange={(selectedGroupIds) =>
                setFormData((prev) => ({
                  ...prev,
                  selectedGroupIds,
                  selectedApartmentIds: [],
                }))
              }
              disabled={formData.sendToAll || !formData.buildingId}
            />

            <ApartmentMultiSelect
              apartments={apartments}
              selectedApartmentIds={formData.selectedApartmentIds}
              selectedGroupIds={formData.selectedGroupIds}
              onChange={(selectedApartmentIds) =>
                setFormData((prev) => ({ ...prev, selectedApartmentIds }))
              }
              disabled={formData.sendToAll || !formData.buildingId}
            />

            {/* Send to All Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="sendToAll"
                checked={formData.sendToAll}
                onChange={handleInputChange}
                className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <span className="ml-3 text-sm text-gray-700">Gửi cho tất cả các căn hộ</span>
            </div>

            {/* Recipient Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Người nhận
              </label>
              <select
                name="recipientType"
                value={formData.recipientType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="OWNER">Chủ căn hộ (mặc định)</option>
                <option value="CO_OWNER">Đồng chủ hộ</option>
                <option value="MEMBER">Thành viên</option>
                <option value="TENANT">Người thuê</option>
                <option value="AUTHORIZED">Ủy quyền</option>
              </select>
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={sending || loading || formData.deliveryMethods.length === 0}
              className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'ĐANG GỬI...' : 'GỬI THÔNG BÁO'}
            </button>
          </div>
        </div>

        {/* Right Column - Notification Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
            Nội dung thông báo
          </h2>
          
          <div className="space-y-4">
            {/* Title Image - App Only */}
            {formData.deliveryMethods.includes('APP') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ảnh tiêu đề (Tùy chọn)
                </label>
                <p className="text-xs text-amber-600 mb-2">
                  ⚠️ Ảnh tiêu đề là 1 ảnh duy nhất và chỉ hiển thị trên App, không hiển thị khi gửi thông báo qua Email.
                </p>
                <input
                  type="url"
                  name="titleImage"
                  value={formData.titleImage}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
            )}

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiêu đề <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">Tối đa 255 ký tự</p>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Nhập tiêu đề thông báo"
                maxLength={255}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                required
              />
              <div className="text-xs text-gray-400 mt-1">
                {formData.subject.length}/255 ký tự
              </div>
            </div>

            {/* Content - React Quill Editor with Tables */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nội dung <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.content}
                  onChange={handleEditorChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Nhập nội dung thông báo..."
                  style={{ 
                    height: '300px',
                    marginBottom: '50px'
                  }}
                  preserveWhitespace
                />
              </div>
              <div className="text-xs text-gray-500 mt-2">
                💡 Mẹo: Dùng nút bảng trong thanh công cụ để tạo bảng chuyên nghiệp. Hỗ trợ copy-paste từ Word/Excel
              </div>
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tệp đính kèm
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-cyan-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">{t('dragDropFiles')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('acceptedFileTypes')}</p>
                </label>
              </div>
              
              {/* Selected Files */}
              {formData.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}; 